import { SolDropdown, SolTextField } from "@solstice/sol-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Dispatch, useContext, useEffect, useState } from "react";
import { BrokerProps, ProfileProps } from "types";
import { useLocation, useParams } from "react-router-dom";
import { GlobalContext, useFeatureFlags, useNavigationHistory } from "context";
import { useApiCall, useSession } from "hooks";
import {
  patchBrokerAPI,
  getIndustryAPI,
  getServiceAPI,
  getPropertyTypeAPI,
  patchProfileAPI,
} from "services";
import queryString from "query-string";
import { URLS, SEARCH_RESULT_LIMIT, STATUS_ACTIVE } from "constant";
import { SelectAutoComplete, Toast } from "ui-atoms";
import { getApiPayloadFromForm, titleToSentenceCase, uniqueItems } from "utils";
import ExpertisePreviousModal from "../Market/ExpertisePreviousModal";
import { LicensePreview } from "../License";

interface IGeneral {
  broker: BrokerProps | null;
  profiles: ProfileProps[] | null;
  setProfiles: Dispatch<React.SetStateAction<ProfileProps[]>>;
  setBroker: Dispatch<React.SetStateAction<BrokerProps | null>>;
}

const INITIAL_VALUES = {
  first_name: "",
  last_name: "",
  email: "",
  telephone: "",
  linkedin: "",
  region: [],
  twitter: "",
  leadership: "",
};

const EXPERTISE_INITIAL_VALUES = {
  industries: [],
  property_types: [],
  services: [],
};

const validateJLLEmail = (email: string) => {
  const regex = /^[a-zA-Z0-9._-]+@jll\.com$/;
  return regex.test(email);
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("This field is required"),
  last_name: Yup.string().required("This field is required"),
  email: Yup.string()
    .required("This field is required")
    .email("Invalid JLL Email")
    .test("jll-email", "Invalid JLL Email", (value: string) => {
      return validateJLLEmail(value);
    }),

  region: Yup.array().required("This field is required"),
});

const expertiseValidationSchema = Yup.object().shape({
  industries: Yup.array()
    .required("This field is required")
    .max(2, "The industries should be limited to a maximum of 2 entries."),
  services: Yup.array()
    .required("This field is required")
    .max(2, "The services should be limited to a maximum of 2 entries."),
  property_types: Yup.array()
    .required("This field is required")
    .max(2, "The property types should be limited to a maximum of 2 entries."),
});

const General: React.FC<IGeneral> = ({
  broker,
  setBroker,
  profiles,
  setProfiles,
}) => {
  const { session } = useSession();
  const { state } = useContext(GlobalContext);
  const { meta } = state;
  const { brokerId } = useParams();
  const location = useLocation();
  const { navigate } = useNavigationHistory();
  const [patchBroker] = useApiCall(patchBrokerAPI);
  const [getIndustry] = useApiCall(getIndustryAPI);
  const [getService] = useApiCall(getServiceAPI);
  const [getPropertyType] = useApiCall(getPropertyTypeAPI);
  const [patchProfile] = useApiCall(patchProfileAPI);
  const [paramFilters, setParamFilters] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { newProfileView } = useFeatureFlags();
  const isAdmin =
    session?.is_superuser ||
    session?.is_staff ||
    session?.permissions?.groups?.includes("Global Admin");
  const leadershipOptions = Object.keys(meta?.broker?.leadership || [])?.map(
    (key: number | string) => ({
      value: Number(key),
      label: meta?.broker?.leadership?.[key]?.value,
    })
  );

  useEffect(() => {
    if (
      !broker ||
      (broker?.pk?.toString() !== brokerId &&
        broker?.id?.toString() !== brokerId)
    )
      return;
    let formValues: any = {};
    Object.keys(INITIAL_VALUES)?.forEach((key: string) => {
      if (key === "region") {
        formValues = {
          ...formValues,
          [key]: (broker as any)?.[key] ? [(broker as any)?.[key]] : [],
        };
        return;
      }
      formValues = {
        ...formValues,
        [key]: (broker as any)?.[key] || "",
      };
    });
    setValues({
      ...formValues,
    });
  }, [broker]);

  // Generate a string of profile IDs to use as a dependency for the useEffect
  const profilesIds = profiles?.map((p) => p?.id).join("-");
  // Update form values for expertise tags sections when profilesIds change
  useEffect(() => {
    if (!profiles || !profiles?.length) return;
    let formValues: any = {};
    Object.keys(EXPERTISE_INITIAL_VALUES)?.forEach((key) => {
      // get unique elements from all profiles
      const ue = uniqueItems(
        profiles.reduce<{ id: string; name: string }[]>(
          (acc, profile) =>
            acc.concat(profile[key as keyof ProfileProps] ?? []),
          []
        ),
        (item) => item?.id
      );
      formValues = {
        ...formValues,
        [key]: ue?.map((item: any) => ({
          value: item?.id,
          label: item?.name,
        })),
      };
    });
    setExpertiseValues(
      {
        ...formValues,
      },
      true
    );
  }, [profilesIds]);

  useEffect(() => {
    const filters = queryString.parse(location?.search, {
      arrayFormat: "bracket",
    });
    setParamFilters(filters);
  }, [location]);

  const { handleBlur, setFieldValue, values, errors, touched, setValues } =
    useFormik({
      initialValues: INITIAL_VALUES,
      validationSchema: validationSchema,
      onSubmit: async () => {},
    });

  const {
    handleBlur: expertiseHandleBlur,
    setFieldValue: expertiseSetFieldValue,
    values: expertiseValues,
    touched: expertiseTouched,
    errors: expertiseErrors,
    setValues: setExpertiseValues,
  } = useFormik({
    initialValues: EXPERTISE_INITIAL_VALUES,
    validationSchema: expertiseValidationSchema,
    onSubmit: async () => {},
  });

  const handleTab = (
    tab: string | number,
    sub: string | number | null,
    mode = "view"
  ) => {
    let updatedFilters = { ...paramFilters };
    updatedFilters["tab"] = tab?.toString();
    if (sub) updatedFilters["sub"] = sub?.toString();
    if (mode === "view") {
      delete updatedFilters["sub"];
      navigate(
        `${URLS.PROFILE.ROOT}/${brokerId}/?${queryString.stringify(
          updatedFilters,
          {
            arrayFormat: "bracket",
          }
        )}`
      );
    } else {
      navigate(
        `${URLS.PROFILE.ROOT}/${brokerId}/edit/?${queryString.stringify(
          updatedFilters,
          {
            arrayFormat: "bracket",
          }
        )}`
      );
    }
  };

  const handleUpdate = (key: string, value: any) => {
    try {
      let payload = getApiPayloadFromForm(broker, { [key]: value });
      if (!payload || !Object.keys(payload)?.length) return;

      if (key === "email" && !validateJLLEmail(value)) return;
      patchBroker({
        pk: broker?.pk || broker?.id,
        ...payload,
      }).then((res: any) => {
        if (!res) return;
        setBroker(res);
      });
    } catch (err) {}
  };

  const handleExpertiseUpdate = async (
    key: keyof typeof EXPERTISE_INITIAL_VALUES,
    values: any
  ) => {
    if (values?.length > 2) {
      Toast.warn(`The ${key} should be limited to a maximum of 2 entries.`);
      return;
    }
    expertiseSetFieldValue(key, values);
    let promises: Promise<any>[] = [];
    // check that we have a payload to send by diffing with each profile
    // it's enough that one profile has a diff to send the patch
    let payloads: [number, any][] = [];
    profiles?.forEach((profile) => {
      const p = getApiPayloadFromForm(profile, {
        [key]: values?.map((item: any) => item?.value),
      });
      if (p && Object.keys(p)?.length) {
        payloads.push([profile.id, p]);
      }
    });
    payloads?.forEach((payload) => {
      promises.push(
        patchProfile({
          pk: payload[0],
          ...payload[1],
        })
      );
    });
    Promise.all(promises).then((res_list) => {
      if (!res_list?.length) return;

      const updatedProfilesMap = new Map(
        res_list.map((profile) => [profile.id, profile])
      );
      setProfiles((prevProfiles) => {
        return prevProfiles.map((profile) =>
          updatedProfilesMap.has(profile.id)
            ? { ...profile, ...updatedProfilesMap.get(profile.id) }
            : profile
        );
      });

      setBroker((prevBroker) => {
        if (!prevBroker) return null;
        const updatedBrokerProfiles = prevBroker?.profiles?.map((profile) =>
          updatedProfilesMap.has(profile.id)
            ? { ...profile, ...updatedProfilesMap.get(profile.id) }
            : profile
        );
        return { ...prevBroker, profiles: updatedBrokerProfiles };
      });
    });
  };

  const loadIndustryOptions = async (
    keyword: string,
    _loadedOptions: any,
    { page }: any
  ) => {
    return await getIndustry({
      keyword,
      page,
      limit: SEARCH_RESULT_LIMIT,
      active_status: [STATUS_ACTIVE],
      sort: "name",
      direction: "asc",
    }).then((res: any) => {
      return {
        options: res.docs.map((industry: any) => {
          return {
            value: industry.pk,
            label: industry?.name,
          };
        }),
        hasMore: page < res?.num_pages,
        additional: { page: page + 1 },
      };
    });
  };

  const loadServiceOptions = async (
    keyword: string,
    _loadedOptions: any,
    { page }: any
  ) => {
    return await getService({
      keyword,
      page,
      limit: SEARCH_RESULT_LIMIT,
      active_status: [STATUS_ACTIVE],
      sort: "name",
      direction: "asc",
    }).then((res: any) => {
      return {
        options: res.docs.map((service: any) => {
          return {
            value: service.pk,
            label: service?.name,
          };
        }),
        hasMore: page < res?.num_pages,
        additional: { page: page + 1 },
      };
    });
  };

  const loadPropertyTypeOptions = async (
    keyword: string,
    _loadedOptions: any,
    { page }: any
  ) => {
    return await getPropertyType({
      keyword,
      page,
      limit: SEARCH_RESULT_LIMIT,
      active_status: [STATUS_ACTIVE],
      sort: "name",
      direction: "asc",
    }).then((res: any) => {
      return {
        options: res.docs.map((propertyType: any) => {
          return {
            value: propertyType.pk,
            label: propertyType?.name,
          };
        }),
        hasMore: page < res?.num_pages,
        additional: { page: page + 1 },
      };
    });
  };

  return (
    <>
      <div className="w-full px-10 pt-8">
        <div className="mb-10 w-full lg:w-1/3">
          <p className="text-sm text-jll-text-base-subdued">
            This information is shared across all your country profiles and
            helps people identify and contact you.
          </p>
        </div>
        <form>
          <div className="flex flex-row items-center mb-10 grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10">
            <SolTextField
              label="First name"
              required
              value={values.first_name}
              onSol-input={(e: any) => setFieldValue("first_name", e?.detail)}
              id="first_name"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("first_name", values.first_name);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("first_name", values.first_name);
                }
              }}
              size="small"
              errorLabel={touched?.first_name ? errors?.first_name : ""}
            />
            <SolTextField
              label="Last name"
              required
              value={values.last_name}
              onSol-input={(e: any) => setFieldValue("last_name", e?.detail)}
              id="last_name"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("last_name", values.last_name);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("last_name", values.last_name);
                }
              }}
              size="small"
              errorLabel={touched?.last_name ? errors?.last_name : ""}
            />
          </div>
          <div className="flex flex-row items-center grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 mb-10">
            <SolTextField
              label="Email"
              value={values.email}
              onSol-input={(e: any) => setFieldValue("email", e?.detail)}
              id="email"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("email", values.email);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("email", values.email);
                }
              }}
              inputFormat="email"
              size="small"
              errorLabel={touched.email ? errors.email : ""}
            />
            <SolTextField
              label="Telephone"
              value={values.telephone}
              onSol-input={(e: any) => setFieldValue("telephone", e?.detail)}
              id="telephone"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("telephone", values.telephone);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("telephone", values.telephone);
                }
              }}
              inputFormat="tel"
              size="small"
              errorLabel={touched?.telephone ? errors?.telephone : ""}
            />
          </div>
          <div className="flex flex-row items-center grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 mb-10">
            <SolDropdown
              id="region"
              size="small"
              label="Region"
              options={Object.keys(meta?.["region"] || [])?.map(
                (key: number | string) => ({
                  value: Number(key),
                  label: meta?.["region"]?.[key],
                })
              )}
              required
              isMultiSelect={false}
              className="!mb-0"
              onUpdate-value={(e) => {
                handleUpdate("region", e?.detail);
              }}
              onBlur={(e) => {
                handleBlur(e);
              }}
              value={values?.region}
              errorLabel={
                touched.region ? (errors.region as string) : undefined
              }
            />
            {isAdmin && (
              <SelectAutoComplete
                name="leadership"
                label="Select Leadership type"
                className="!mb-0 -mt-2.5"
                size="sm"
                options={leadershipOptions}
                value={leadershipOptions?.find(
                  (item: any) => item?.value === values?.leadership
                )}
                onChange={(e) => {
                  setFieldValue("leadership", e?.value);
                  handleUpdate("leadership", e?.value || null);
                }}
                onBlur={(e: any) => {
                  handleBlur(e);
                }}
                error={
                  touched.leadership ? (errors.leadership as string) : undefined
                }
              />
            )}
          </div>
          <div className="flex flex-row items-center grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 mb-10">
            <SolTextField
              label="Linkedin URL"
              value={values.linkedin}
              onSol-input={(e: any) => setFieldValue("linkedin", e?.detail)}
              id="linkedin"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("linkedin", values.linkedin);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("linkedin", values.linkedin);
                }
              }}
              size="small"
              errorLabel={touched?.linkedin ? errors?.linkedin : ""}
            />
            <SolTextField
              label="Twitter URL"
              value={values.twitter}
              onSol-input={(e: any) => setFieldValue("twitter", e?.detail)}
              id="twitter"
              onBlur={(e) => {
                handleBlur(e);
                handleUpdate("twitter", values.twitter);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleUpdate("twitter", values.twitter);
                }
              }}
              size="small"
              errorLabel={touched?.twitter ? errors?.twitter : ""}
            />
            <div />
          </div>
        </form>
        <div className="mb-12">
          <h2 className="text-xl mb-1">Expertise Tags</h2>
          <p className="text-sm text-jll-text-base-subdued">
            Choose up to two tags per category that best represent your areas of
            expertise. If you do not have a specialization, or if you are in a
            leadership role, you may leave these fields blank. Tags will appear
            in the 'Expertise' section of your profile. Click{" "}
            <button
              className="underline cursor-pointer"
              onClick={() => setIsOpen(true)}
            >
              here
            </button>{" "}
            for an example.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-10 mb-8">
          <div className="cols-span-1">
            <p className="mb-1">Industry</p>
            <p className="text-sm text-jll-text-base-subdued mb-2">
              Broad economic sectors for which JLL invests sales and marketing
              resources.
            </p>
            <SelectAutoComplete
              name="industries"
              loadOptions={loadIndustryOptions}
              isMulti
              size="sm"
              value={expertiseValues.industries}
              onChange={(values) => {
                handleExpertiseUpdate("industries", values);
              }}
              onBlur={(e: any) => {
                expertiseHandleBlur(e);
              }}
              className=" w-full"
              error={
                expertiseTouched?.industries || expertiseErrors?.industries
                  ? expertiseErrors?.industries
                  : ""
              }
            />
          </div>
          <div className="cols-span-1">
            <p className="mb-1">Services</p>
            <p className="text-sm text-jll-text-base-subdued mb-2">
              Strategically important JLL service offerings.
            </p>
            <SelectAutoComplete
              name="services"
              loadOptions={loadServiceOptions}
              isMulti
              size="sm"
              value={expertiseValues?.services}
              onChange={(values) => {
                handleExpertiseUpdate("services", values);
              }}
              onBlur={(e: any) => {
                expertiseHandleBlur(e);
              }}
              className="cols-span-1 w-full"
              error={
                expertiseTouched?.services || expertiseErrors?.services
                  ? expertiseErrors?.services
                  : ""
              }
            />
          </div>
          <div className="cols-span-1">
            <p className="mb-1">Property Type</p>
            <p className="text-sm text-jll-text-base-subdued mb-2">
              Real estate property types (also known as asset classes or asset
              types).
            </p>
            <SelectAutoComplete
              name="property_types"
              loadOptions={loadPropertyTypeOptions}
              isMulti
              size="sm"
              value={expertiseValues?.property_types}
              onChange={(values) => {
                handleExpertiseUpdate("property_types", values);
              }}
              onBlur={(e: any) => {
                expertiseHandleBlur(e);
              }}
              className="cols-span-1 w-full"
              error={
                expertiseTouched?.property_types ||
                expertiseErrors?.property_types
                  ? expertiseErrors?.property_types
                  : ""
              }
            />
          </div>
        </div>
        {newProfileView && (
          <div className="mb-20">
            <div className="mb-12">
              <h2 className="text-xl mb-1">License data</h2>
              <p className="text-sm text-jll-text-base-subdued">
                If license information exists in WorkDay, it will sync
                automatically to Profile Management.
                <br />
                Please visit WorkDay to update license information.
              </p>
            </div>
            <LicensePreview licenses={broker?.licenses || undefined} />
          </div>
        )}
      </div>
      <ExpertisePreviousModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        profiles={profiles}
      />
    </>
  );
};

export default General;
